import { useState, useCallback, useEffect } from 'react';
import { LinkedInConnection } from '../types';
import { apiClient } from '../lib/api';
import { useToast } from './useToast';

/**
 * Hook for managing LinkedIn connections
 */
export const useLinkedInConnections = () => {
  const { showToast } = useToast();
  const [connections, setConnections] = useState<LinkedInConnection[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isValidating, setIsValidating] = useState<string | null>(null);
  const [disconnectingId, setDisconnectingId] = useState<string | null>(null);

  const loadConnections = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await apiClient.getAllLinkedInConnections();
      setConnections(response.connections || []);
    } catch (error) {
      console.error('Failed to load LinkedIn connections:', error);
      showToast('Failed to load LinkedIn connections', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    loadConnections();
  }, [loadConnections]);

  const validateToken = useCallback(
    async (connectionId: string) => {
      try {
        setIsValidating(connectionId);
        const result = await apiClient.validateLinkedInToken(connectionId);
        if (result.valid) {
          showToast('LinkedIn token validated successfully', 'success');
          loadConnections();
        } else {
          showToast(result.error || 'Token validation failed', 'error');
        }
      } catch (error) {
        console.error('Failed to validate LinkedIn token:', error);
        showToast('Failed to validate LinkedIn token', 'error');
      } finally {
        setIsValidating(null);
      }
    },
    [loadConnections, showToast]
  );

  const disconnect = useCallback(
    async (connectionId: string) => {
      try {
        setDisconnectingId(connectionId);
        await apiClient.disconnectLinkedIn(connectionId);
        showToast('LinkedIn account disconnected', 'success');
        loadConnections();
      } catch (error) {
        console.error('Failed to disconnect LinkedIn:', error);
        showToast('Failed to disconnect LinkedIn account', 'error');
      } finally {
        setDisconnectingId(null);
      }
    },
    [loadConnections, showToast]
  );

  return {
    connections,
    isLoading,
    isValidating,
    disconnectingId,
    validateToken,
    disconnect,
    refresh: loadConnections,
  };
};
